import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Link, Typography } from '@mui/material';
import MyLocationRounded from '@mui/icons-material/MyLocationRounded';
import { Autocomplete } from '@react-google-maps/api';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import Button from './Button';
import CheckoutAddresses from './CheckoutAddresses';
import {
  checkPermission,
  getCurrentPosition,
  requestLocationPermission,
} from '../platform/geolocation';
import { getPilotUserAddresses } from '../services/addresses';
import { resolveDeliveryContext } from '../services/locationResolve';
import { useV2GoogleMaps } from '../state/GoogleMapsProvider';
import { useV2Session } from '../state/SessionProvider';

const searchInputStyle = {
  boxSizing: 'border-box',
  border: '1px solid #ccc',
  width: '100%',
  maxWidth: '100%',
  height: 44,
  padding: '0 12px',
  borderRadius: 4,
  fontSize: 14,
  outline: 'none',
};

function LoginForSavedAddressesHint() {
  const location = useLocation();

  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      <Link
        component={RouterLink}
        to="/v2/auth"
        state={{ from: location }}
        underline="hover"
        fontWeight={600}
      >
        Log in
      </Link>
      {' '}
      to see your saved addresses.
    </Typography>
  );
}

function GpsLocationSection({ onDetect }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const onClick = async () => {
    setBusy(true);
    setError('');
    try {
      await onDetect();
    } catch (e) {
      setError(e?.message || 'No store found for this area');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Button
        variant="outlined"
        fullWidth
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <MyLocationRounded />}
        disabled={busy}
        onClick={onClick}
        sx={{ borderRadius: '5px' }}
      >
        {busy ? 'Locating…' : 'Use current location'}
      </Button>
    </Box>
  );
}

/** Google Places search for guests — pick an area from suggestions. */
function GuestAreaSearch({ onPlaceResolved }) {
  const [autocomplete, setAutocomplete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { mapKey, isLoaded, loadError } = useV2GoogleMaps();

  const onPlaceChanged = useCallback(async () => {
    if (!autocomplete || busy) return;
    const place = autocomplete.getPlace();
    const loc = place?.geometry?.location;
    if (!loc) {
      setError('Select an area from the suggestions list.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const areaLabel =
        place.name ||
        place.formatted_address ||
        place.vicinity ||
        null;
      await resolveDeliveryContext({
        userPos: { lat: loc.lat(), lng: loc.lng() },
        addresses: [],
        loggedIn: false,
        gpsOnly: true,
        areaLabel,
      });
      await onPlaceResolved?.();
    } catch (e) {
      setError(e?.message || 'No store found for this area');
    } finally {
      setBusy(false);
    }
  }, [autocomplete, busy, onPlaceResolved]);

  if (!mapKey) {
    return (
      <Alert severity="info" sx={{ mb: 0 }}>
        Area search is not configured. Use current location below.
      </Alert>
    );
  }

  if (loadError) {
    return (
      <Alert severity="warning" sx={{ mb: 0 }}>
        Could not load Google Maps. Use current location below.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        Search your area
      </Typography>
      {!isLoaded ? (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ position: 'relative', opacity: busy ? 0.6 : 1, pointerEvents: busy ? 'none' : 'auto' }}>
          <Autocomplete
            onLoad={setAutocomplete}
            onPlaceChanged={onPlaceChanged}
            options={{
              componentRestrictions: { country: 'in' },
              fields: ['geometry', 'name', 'formatted_address', 'vicinity'],
            }}
          >
            <input
              type="text"
              placeholder="Search for your area"
              style={searchInputStyle}
              disabled={busy}
            />
          </Autocomplete>
          {busy ? (
            <Box
              sx={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <CircularProgress size={18} />
            </Box>
          ) : null}
        </Box>
      )}
      {error ? <Alert severity="error">{error}</Alert> : null}
    </Box>
  );
}

function GuestLocationPicker({ onGpsDetect, onResolved }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <LoginForSavedAddressesHint />
      <GuestAreaSearch onPlaceResolved={onResolved} />
      <Typography variant="caption" color="text.secondary" textAlign="center">
        or
      </Typography>
      <GpsLocationSection onDetect={onGpsDetect} />
    </Box>
  );
}

/**
 * Address / GPS picker body (modal or page).
 * @param {{ addresses?: Array, onResolved?: () => void }} props
 */
export default function LocationPickerContent({ addresses: addressesProp, onResolved }) {
  const { loggedIn, mobile } = useV2Session();
  const [addresses, setAddresses] = useState(addressesProp ?? []);
  const [addressListKey, setAddressListKey] = useState(0);

  useEffect(() => {
    if (addressesProp) {
      setAddresses(addressesProp);
    }
  }, [addressesProp]);

  useEffect(() => {
    if (addressesProp?.length || !loggedIn || !mobile) return undefined;

    let cancelled = false;
    getPilotUserAddresses(mobile)
      .then((list) => {
        if (!cancelled) setAddresses(list);
      })
      .catch(() => {
        if (!cancelled) setAddresses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [addressesProp, loggedIn, mobile]);

  const handleAddressResolved = useCallback(() => {
    onResolved?.();
  }, [onResolved]);

  const handleGpsDetect = useCallback(async () => {
    let perm = await checkPermission();
    if (perm === 'prompt') {
      perm = await requestLocationPermission();
    }
    if (perm !== 'granted') {
      throw new Error('Location permission is required.');
    }
    const pos = await getCurrentPosition();
    if (!pos) {
      throw new Error('Could not read your location.');
    }
    await resolveDeliveryContext({
      userPos: pos,
      addresses,
      loggedIn,
      gpsOnly: true,
    });
    setAddressListKey((k) => k + 1);
    onResolved?.();
  }, [addresses, loggedIn, onResolved]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {loggedIn ? (
        <>
          <GpsLocationSection onDetect={handleGpsDetect} />
          <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <CheckoutAddresses
              key={addressListKey}
              heading="Saved addresses"
              onAfterLocationMerge={({ storeId }) => {
                if (storeId) handleAddressResolved();
              }}
            />
          </Box>
        </>
      ) : (
        <GuestLocationPicker onGpsDetect={handleGpsDetect} onResolved={handleAddressResolved} />
      )}
    </Box>
  );
}
